import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Folder, Cart, Settings, Bell, Cube, PageEdit } from 'iconoir-react';

interface MenuItem {
  path: string;
  label: string;
  icon: React.ComponentType<any>;
  children?: MenuItem[];
}

const menuItems: MenuItem[] = [
  {
    path: '/dossier',
    label: 'Dossiers',
    icon: Folder,
  },
  {
    path: '/commande',
    label: 'Commandes',
    icon: Cart,
  },
  {
    path: '/stock',
    label: 'Stock',
    icon: Cube,
  },
  {
    path: '/inventaire',
    label: 'Inventaire',
    icon: Cube,
  },
  {
    path: '/administration',
    label: 'Administration',
    icon: Settings,
    children: [
      {
        path: '/administration/console-regles',
        label: 'Console règles',
        icon: PageEdit,
      },
      {
        path: '/administration/notification',
        label: 'Notification',
        icon: Bell,
      },
    ],
  },
];

const MenuWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0;
`;

const MenuItemContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  text-decoration: none;
  color: ${({ theme, $isActive }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : (theme.newUI.defaultColors.text || '#333')};
  background-color: ${({ $isActive }) => $isActive ? 'rgba(33, 150, 243, 0.1)' : 'transparent'};
  border-left: 3px solid ${({ $isActive, theme }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : 'transparent'};
  transition: all 0.2s ease;
  font-weight: ${({ $isActive }) => $isActive ? 600 : 400};
  
  &:hover {
    background-color: ${({ theme }) => theme.newUI.variationColors?.light5 || '#f5f5f5'};
    color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  }
`;

const MenuItemButton = styled.button<{ $isActive: boolean; $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  text-decoration: none;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  color: ${({ theme, $isActive }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : (theme.newUI.defaultColors.text || '#333')};
  background-color: ${({ $isActive }) => $isActive ? 'rgba(33, 150, 243, 0.1)' : 'transparent'};
  border-left: 3px solid ${({ $isActive, theme }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : 'transparent'};
  transition: all 0.2s ease;
  font-weight: ${({ $isActive }) => $isActive ? 600 : 400};
  
  &:hover {
    background-color: ${({ theme }) => theme.newUI.variationColors?.light5 || '#f5f5f5'};
    color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  }
`;

const ChevronIcon = styled.span<{ $isExpanded: boolean }>`
  margin-left: auto;
  transition: transform 0.2s ease;
  transform: ${({ $isExpanded }) => $isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.newUI.variationColors?.dark2 || '#666'};
`;

const SubMenuWrapper = styled.div<{ $isExpanded: boolean }>`
  display: ${({ $isExpanded }) => $isExpanded ? 'flex' : 'none'};
  flex-direction: column;
  background-color: ${({ theme }) => theme.newUI.variationColors?.light5 || '#f9f9f9'};
`;

const SubMenuItem = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem 0.75rem 3.5rem;
  text-decoration: none;
  color: ${({ theme, $isActive }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : (theme.newUI.variationColors?.dark1 || '#555')};
  background-color: ${({ $isActive }) => $isActive ? 'rgba(33, 150, 243, 0.1)' : 'transparent'};
  border-left: 3px solid ${({ $isActive, theme }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : 'transparent'};
  transition: all 0.2s ease;
  font-weight: ${({ $isActive }) => $isActive ? 600 : 400};
  font-size: 0.9rem;
  
  &:hover {
    background-color: ${({ theme }) => theme.newUI.variationColors?.light4 || '#f0f0f0'};
    color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  }
`;

const Icon = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${({ theme, $isActive }) => 
    $isActive 
      ? (theme.colors?.primary || '#2196f3')
      : (theme.newUI.variationColors?.dark2 || '#666')};
  
  ${MenuItem}:hover &,
  ${MenuItemButton}:hover &,
  ${SubMenuItem}:hover & {
    color: ${({ theme }) => theme.colors?.primary || '#2196f3'};
  }
`;

const Label = styled.span`
  font-size: 0.95rem;
`;

interface SidebarMenuProps {
  onItemClick?: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ onItemClick }) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (path: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const isChildPathActive = (childPath: string): boolean => {
    return (
      location.pathname === childPath ||
      location.pathname.startsWith(`${childPath}/`)
    );
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (location.pathname === item.path) {
      return true;
    }
    if (item.children) {
      return item.children.some((child) => isChildPathActive(child.path));
    }

    return false;
  };

  const isExpanded = (item: MenuItem): boolean => {
    if (expandedItems.has(item.path)) return true;
    if (item.children) {
      return item.children.some((child) => isChildPathActive(child.path));
    }
    return false;
  };

  return (
    <MenuWrapper>
      {menuItems.map((item) => {
        const isActive = isItemActive(item);
        const IconComponent = item.icon;
        const hasChildren = item.children && item.children.length > 0;
        const isExpandedItem = isExpanded(item);

        if (hasChildren) {
          return (
            <MenuItemContainer key={item.path}>
              <MenuItemButton
                $isActive={isActive}
                $isExpanded={isExpandedItem}
                onClick={() => toggleExpanded(item.path)}
                type="button"
              >
                <Icon $isActive={isActive}>
                  <IconComponent width={24} height={24} />
                </Icon>
                <Label>{item.label}</Label>
                <ChevronIcon $isExpanded={isExpandedItem}>▶</ChevronIcon>
              </MenuItemButton>
              <SubMenuWrapper $isExpanded={isExpandedItem}>
                {item.children?.map((child) => {
                  const isChildActive = isChildPathActive(child.path);
                  const ChildIconComponent = child.icon;
                  return (
                    <SubMenuItem
                      key={child.path}
                      to={child.path}
                      $isActive={isChildActive}
                      onClick={onItemClick}
                    >
                      <Icon $isActive={isChildActive}>
                        <ChildIconComponent width={20} height={20} />
                      </Icon>
                      <Label>{child.label}</Label>
                    </SubMenuItem>
                  );
                })}
              </SubMenuWrapper>
            </MenuItemContainer>
          );
        }

        return (
          <MenuItem
            key={item.path}
            to={item.path}
            $isActive={isActive}
            onClick={onItemClick}
          >
            <Icon $isActive={isActive}>
              <IconComponent width={24} height={24} />
            </Icon>
            <Label>{item.label}</Label>
          </MenuItem>
        );
      })}
    </MenuWrapper>
  );
};

export default SidebarMenu;
